import z from 'zod'

import { type DocumentResponse, DocumentResponseSchema } from './documents'
import { type NoteResponse } from './notes'

export const TimelineEventSchema = DocumentResponseSchema.shape.activities.element.extend({
  // Source linkage: an event flattened from a document carries the document id(s),
  // one flattened from a note carries the note id(s). Both are optional.
  documents: z.array(z.string()).optional(),
  notes: z.array(z.string()).optional(),
  endDate: z.string().optional(),
})

export const TimelineDocumentSchema = z.object({
  filePath: z.string(),
  id: z.string(),
  mimeType: z.string(),
  name: z.string(),
  type: DocumentResponseSchema.shape.type,
})

export const TimelineNoteSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
})

export const TimelineResponseSchema = z.object({
  documents: z.record(z.string(), TimelineDocumentSchema),
  notes: z.record(z.string(), TimelineNoteSchema),
  events: z.array(TimelineEventSchema),
})

export type TimelineEvent = z.infer<typeof TimelineEventSchema>

export type TimelineDocument = z.infer<typeof TimelineDocumentSchema>

export type TimelineNote = z.infer<typeof TimelineNoteSchema>

export type TimelineResponse = z.infer<typeof TimelineResponseSchema>

// Builds the unified timeline from a person's documents AND their notes. Both
// sources contribute activities (identical element shape); each event is tagged
// with its source id(s) so the UI can link back to the document or note.
export const buildTimeline = (
  documentData: DocumentResponse[],
  noteData: NoteResponse[] = [],
): TimelineResponse => {
  const documentEvents = documentData.flatMap(doc =>
    doc.activities.map(
      act =>
        TimelineEventSchema.parse({
          ...act,
          date: getEventDate(act).toISOString(),
          documents: [doc.id],
        }) satisfies TimelineEvent,
    ),
  )

  const noteEvents = noteData.flatMap(note =>
    note.activities.map(
      act =>
        TimelineEventSchema.parse({
          ...act,
          date: getEventDate(act).toISOString(),
          notes: [note.id],
        }) satisfies TimelineEvent,
    ),
  )

  const documents = documentData.map(doc => {
    const primary = doc.files[0]
    return TimelineDocumentSchema.parse({
      filePath: primary?.filePath ?? '',
      id: doc.id,
      mimeType: primary?.mimeType ?? '',
      name: doc.name,
      type: doc.type,
    }) satisfies TimelineDocument
  })

  const notes = noteData.map(note =>
    TimelineNoteSchema.parse({ id: note.id, title: note.title }) satisfies TimelineNote,
  )

  return TimelineResponseSchema.parse({
    documents: Object.fromEntries(documents.map(doc => [doc.id, doc])),
    notes: Object.fromEntries(notes.map(note => [note.id, note])),
    events: [...documentEvents, ...noteEvents].sort(sortEventsByDateDesc),
  } satisfies TimelineResponse)
}

const getEventDate = (event: {
  date?: string
  day?: number
  month?: number
  year: number
}): Date => {
  if (event.date) return new Date(event.date)

  const date = new Date(event.year, 0, 1)
  if (event.month) date.setMonth(event.month - 1)
  if (event.day) date.setDate(event.day)

  return date
}

const sortEventsByDateDesc = (a: TimelineEvent, b: TimelineEvent): number => {
  const dateA = new Date(a.date!).getTime()
  const dateB = new Date(b.date!).getTime()

  return dateB - dateA
}
